<template>
  <div>
    <project-actions></project-actions>
    <div id="map" @keydown="onkeydown($event)"></div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import axios from "axios";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import mapboxgl from "mapbox-gl";
import DrawRectangle from "mapbox-gl-draw-rectangle-mode";
import ProjectActions from "./components/project-actions.component";

export default {
  components: {
    ProjectActions,
  },
  async mounted() {
    await this.init_map();
    this.map.on("draw.modechange", (e) => {
      if (
        this.current_mode &&
        this.current_mode.startsWith("draw") &&
        e.mode == "simple_select" &&
        this.is_continuous_mode
      ) {
        this.draw.changeMode(this.current_mode);
      } else {
        this.$store.dispatch("set_current_mode", e.mode);
      }
    });
    this.map.on("style.load", () => {
      this.$store.dispatch("init_feature_colors", this.stops);
    });
    this.map.on("load", () => {
      var geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
      });
      this.map.addControl(geocoder, "top-right");
      this.$store.dispatch("init_draw", this.init_draw());
      this.map.addControl(this.draw);
      this.$store.dispatch("load_sources");
    });
    this.map.on("draw.selectionchange", (e) => {
      this.$store.dispatch("set_selected_features", e.features);
    });
    this.map.on("draw.create", (e) => {
      this.$store.dispatch("update_vector_count");
      this.revert_non_editable_features();
    });
    this.map.on("draw.update", (e) => {
      this.$store.dispatch("update_vector_count");
      this.revert_non_editable_features();
    });
  },
  computed: {
    ...mapGetters([
      "map",
      "draw",
      "vectors",
      "current_mode",
      "is_continuous_mode",
      "features",
      "selected_features",
    ]),
    stops() {
      let arr = [];
      Object.values(this.vectors).forEach((v) => {
        arr.push([v.class_id, v.color ? v.color : 'black']);
      });
      return arr;
    },
    shortcuts() {
      let arr = [];
      let shortcut_map = {
        100: 0,
        101: 1,
        102: 2,
        103: 3,
        104: 4,
        105: 5,
        106: 6,
        107: 7,
        108: 8,
        109: 9,
      };
      Object.values(this.vectors).forEach((v) => {
        if (v.num_shortcut) {
          arr[v.num_shortcut] = [v.class_id, v.class_name];
        }
        if (shortcut_map[v.class_id] >= 0) {
          arr[shortcut_map[v.class_id]] = [v.class_id, v.class_name];
        }
      });
      return arr;
    },
  },
  watch: {
    vectors: function (val) {
      if (val) {
        setTimeout(() => {
          this.$store.dispatch("init_feature_colors", this.stops);
        }, 1000);
      }
    },
    draw: function (val) {
      if (val) {
        this.$store.dispatch("init_feature_colors", this.stops);
      }
    },
  },
  methods: {
    async init_map() {

      mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_ACCESS_TOKEN;

      this.$store.dispatch(
        "init_map",
        new mapboxgl.Map({
          container: "map",
          style: "mapbox://styles/mapbox/satellite-v8",
          center: [77.587489, 12.913538],
          zoom: 16,
          maxZoom: 24,
          attributionControl: false,
        })
      );
    },
    init_draw() {
      const modes = MapboxDraw.modes;
      modes.draw_rectangle = DrawRectangle;
      return new MapboxDraw({
        keybindings: false,
        userProperties: true,
        displayControlsDefault: true,
        controls: {
          polygon: true,
          rectangle: true,
          trash: true,
        },
        modes: modes,
      });
    },
    changeClass(num) {
      if (this.shortcuts[num]) {
        for (let id of this.draw.getSelectedIds()) {
          this.draw.setFeatureProperty(id, "class_id", this.shortcuts[num][0]);
          this.draw.setFeatureProperty(
            id,
            "class_name",
            this.shortcuts[num][1]
          );
        }
      }
      this.$store.dispatch("update_vector_count");
      this.$store.dispatch(
        "set_selected_features",
        this.draw.getSelected().features
      );
    },
    onkeydown(e) {
      switch (e.key) {
        case "c": {
          this.$store.dispatch("toggle_continuous_mode");
          this.draw.changeMode("simple_select");
          break;
        }
        case "r": {
          this.$store.dispatch("set_current_mode", "draw_rectangle");
          this.draw.changeMode("draw_rectangle");
          break;
        }
        case "p": {
          this.$store.dispatch("set_current_mode", "draw_polygon");
          this.draw.changeMode("draw_polygon");
          break;
        }
        case "m": {
          this.$store.dispatch("set_current_mode", "draw_point");
          this.draw.changeMode("draw_point");
          break;
        }
        case "l": {
          this.$store.dispatch("set_current_mode", "draw_line_string");
          this.draw.changeMode("draw_line_string");
          break;
        }
        case "Escape":
          this.draw.changeMode("simple_select");
          break;
        case "Delete":
          this.draw.trash();
          this.$store.dispatch("update_vector_count");
          break;
        case " ":
          this.duplicateFeatures();
          break;
        case "0":
        case "1":
        case "2":
        case "3":
        case "4":
        case "5":
        case "6":
        case "7":
        case "8":
        case "9":
          this.changeClass(e.key);
      }
    },
    duplicateFeatures() {
      let { features } = this.draw.getSelected();
      for (let feature of features) {
        delete feature["id"];
        delete feature.properties.editable;
        // delete conflicting properties to support therm.
        delete feature.properties.row;
        delete feature.properties.column;
        delete feature.properties.table_row;
        delete feature.properties.table_column;
        delete feature.properties.idx;
        delete feature.properties.num_modules_affected;
        delete feature.properties.uid;

        this.draw.add(feature);
      }
    },
    revert_non_editable_features() {
      if (this.selected_features && this.selected_features.length)
        for (let feature of this.selected_features) {
          if (feature.properties.editable === false) this.draw.add(feature);
        }
    },
  },
};
</script>
<style lang="scss">
#map {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}
</style>
